import { useTenantApi } from '@/account/hook/useTenantApi';
import { listOrders, listOrdersQueryKey, loadRegistrationInfo, registrationInfoQueryKey, registrationQueryKey, type Order } from '@/api/registration';
import { type ApiResponse } from '@/api/tenantClient';
import QueryContainer, { StandardSpinner } from '@/base/QueryContainer/QueryContainer';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import { UiSpinner, UiStack, uiStyles, UiText, UiVStack } from '@/lib/ui';
import InfoBox from '@/registration/component/InfoBox';
import RegisterLayout from '@/registration/component/Register/Layout';
import Header from '@/registration/component/Register/Layout/Header';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FC } from 'react';
import AdditionalInfoSession from './AdditionalInfoSession';
import OrderList from './OrderList';
import PersonalInfoSession from './PersonalInfoSession';
import SessionAddonList from './SessionAddonList';
import { UiTab, UiTabList, UiTabPanel, UiTabPanels, UiTabs } from '@/lib/ui/Tabs';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import GroupRegistrationDetail from './GroupRegistrationDetail';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';
import { Box } from '@chakra-ui/react';
import PrepareDetails from './PrepareDetails';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import RegistrationType from './RegistrationType';

const TABS = {
  NONE: -1,
  MY_REGISTRATION: 0,
  MY_GROUP_REGISTRATION: 1
};

const RegistrationDetails: FC = () => {
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const [selectedTab, setSelectedTab] = useState(TABS.NONE);
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { data: eventSetting } = useEventSettingsQuery(eventId);
  const confirmationMetadata = eventSetting?.pageMetadata?.confirmation;
  const { userSession } = useUserSession();
  const { data: orderTypes } = useQuery({
    queryKey: [registrationInfoQueryKey, { eventId }],
    queryFn: async () => { return await loadRegistrationInfo(createTenantApiRequest)(eventId); },
    enabled: !!eventId,
  });

  const registrationQuery = useRegistrationQuery(eventId, { enabled: !!orderTypes?.item?.existRegistration });
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId, { enabled: !!orderTypes?.item?.existGroupRegistration });
  const orderListQuery = useQuery<ApiResponse<Order>, Error>({
    queryKey: [listOrdersQueryKey, { eventId, userSession }],
    queryFn: async () => {
      // console.log('----list orders', orderTypes);
      return await listOrders(createTenantApiRequest)({ eventId });
    },
    enabled: !!eventId && !isApiPreparing && !!orderTypes,
    onSuccess: async () => {
      const queriesToInvalidate = [];
      if (orderTypes?.item?.existGroupRegistration) {
        queriesToInvalidate.push(queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }, { token: userSession?.authToken }] }));
      }
      if (orderTypes?.item?.existRegistration) {
        queriesToInvalidate.push(queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] }));
      }
      await Promise.all(queriesToInvalidate);
    },
    staleTime: 0
  });

  useEffect(() => {
    if (selectedTab === TABS.NONE && orderListQuery.isSuccess) {
      if (registrationQuery.data?.mainEventTicket) {
        setSelectedTab(TABS.MY_REGISTRATION);
      } else if ((groupRegistrationQuery.data?.registrations ?? []).length > 0) {
        setSelectedTab(TABS.MY_GROUP_REGISTRATION);
      }
    }
  }, [registrationQuery.data, groupRegistrationQuery.data, selectedTab, orderListQuery.isSuccess]);

  if (registrationQuery.isFetching || groupRegistrationQuery.isFetching) {
    return <PrepareDetails registrationQuery={registrationQuery} groupRegistrationQuery={groupRegistrationQuery} />;
  }

  if (orderListQuery.isLoading) {
    return <RegisterLayout showShoppingCart={false}>
      <UiVStack spacing={4}>
        <UiSpinner />
        <UiText variant={'h6'}>We&apos;re completing your order, please wait</UiText>
      </UiVStack>
    </RegisterLayout>;
  }

  return (
    <RegisterLayout header={<Header />}>
      <UiStack spacing={8} pb={16}>
        {(confirmationMetadata?.title || confirmationMetadata?.message) && (
          <UiStack spacing={4}>
            {confirmationMetadata?.title && (
              <InfoBox title={confirmationMetadata.title} />
            )}
            {confirmationMetadata?.message && (
              <BaseMessageBarInfo borderRadius={uiStyles.borderRadius} bgColor={'green.100'}>
                {confirmationMetadata.message}
              </BaseMessageBarInfo>
            )}
          </UiStack>
        )}
        <UiStack>
          <OrderList orderListQuery={orderListQuery} emailOwner={userSession?.email} />
        </UiStack>
        <BaseDividerHorizontal height={0} />
        <QueryContainer query={registrationQuery}>
          {(registration) => {
            return registrationQuery.isLoading ? (
              <UiSpinner />
            ) : (
              <UiStack
                flexGrow={1}
                spacing={4}
                enableDivider={false}
              >
                <UiText variant={'title'}>
                  My registration
                </UiText>
                <UiStack
                  borderRadius={uiStyles.borderRadius}
                  bgColor={'#fff'}
                  shadow={uiStyles.cardShadow}
                  px={{ base: 4, xl: 8 }}
                  py={8}
                  spacing={8}
                >
                  <RegistrationType registration={registration} />
                  <SessionAddonList orderListQuery={orderListQuery} registration={registration}/>
                  <PersonalInfoSession registration={registration} />
                  <AdditionalInfoSession registration={registration} />
                </UiStack>
              </UiStack>
            );
          }}
        </QueryContainer>
        {groupRegistrationQuery.isFetching ? (
          <UiStack>
            <StandardSpinner />
          </UiStack>
        ) : (
          <>
            <BaseDividerHorizontal height={0} />
            <UiStack
              flexGrow={1}
              spacing={4}
              enableDivider={false}
            >
              <UiText variant={'title'}>
                My group registration
              </UiText>
              <UiStack
                borderRadius={uiStyles.borderRadius}
                bgColor={'#fff'}
                shadow={uiStyles.cardShadow}
                px={{ base: 4, xl: 8 }}
                py={8}
                spacing={8}
              >
                <GroupRegistrationDetail groupRegistrationQuery={groupRegistrationQuery} />
              </UiStack>
            </UiStack>
          </>
        )}
      </UiStack>

      {/*<UiTabs variant='soft-rounded' onChange={setSelectedTab} index={selectedTab}>*/}


      {/*  <UiTabList>*/}
      {/*    <UiTab isDisabled={!orderTypes?.item?.existRegistration}>*/}
      {/*      My registration*/}
      {/*    </UiTab>*/}
      {/*    <UiTab isDisabled={!orderTypes?.item?.existGroupRegistration}>*/}
      {/*      My group registration*/}
      {/*    </UiTab>*/}
      {/*  </UiTabList>*/}
      {/*  <UiTabPanels>*/}
      {/*    <UiTabPanel>*/}
      {/*      <QueryContainer query={registrationQuery}>*/}
      {/*        {(registration) => {*/}
      {/*          return registrationQuery.isLoading ? <UiSpinner /> : <BaseTransitionSlideLeft>*/}
      {/*            <UiStack flexGrow={1} spacing={8} enableDivider={false} pb={16} width='100%'>*/}
      {/*              <SessionAddonList orderListQuery={orderListQuery} registration={registration}/>*/}
      {/*              <PersonalInfoSession registration={registration} />*/}
      {/*              <AdditionalInfoSession registration={registration} />*/}
      {/*            </UiStack>*/}
      {/*          </BaseTransitionSlideLeft>;*/}
      {/*        }}*/}
      {/*      </QueryContainer>*/}
      {/*    </UiTabPanel>*/}
      {/*    <UiTabPanel>*/}
      {/*      {groupRegistrationQuery.isLoading ? <UiSpinner /> : <GroupRegistrationDetail groupRegistrationQuery={groupRegistrationQuery} />}*/}
      {/*    </UiTabPanel>*/}
      {/*  </UiTabPanels>*/}
      {/*</UiTabs>*/}
    </RegisterLayout >
  );
};

export default RegistrationDetails;
